import { useCallback, useContext, useMemo, useState } from 'react'

import { theme } from '@hhgtech/hhg-components'
import { MobileBottomNavigation } from '@hhgtech/hhg-components/components'
import { LOCALE_HAS_COMMUNITY_SITE } from '@hhgtech/hhg-components/constants'
import { Text } from '@hhgtech/hhg-components/mantine'
import { Home, ChatAi, ChatBubble } from '@hhgtech/icons/other'
import { Box, rem } from '@mantine/core'
import { useClickOutside } from '@mantine/hooks'
import throttle from 'lodash/throttle'
import dynamic from 'next/dynamic'
import { shallow } from 'zustand/shallow'

import AirPopulation from 'components/molecules/AirPopulation'
import useLatestTotalComment from 'components/pages/article/components/useLatestTotalComment'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { useSpacingSubotStickyBar } from 'hooks/useSpacingSubotStickyBar'
import { CHAT_AI_PATH, HAS_CHAT_AI_SITE } from 'interfaces/constants'
import { ALLOW_AIR_POPUPLATION } from 'interfaces/constants/configs'
import { LOCALE } from 'interfaces/types'
import { AppContext } from 'state/appContext'
import { useArticleStore } from 'state/article'

import { CommonStyle } from './commonStyles'
import Wrapper from './Wrapper'
import { SharePopupNew } from '../sharePopupNew'

const ArticleAudioPlayer = dynamic(() =>
  import('components/molecules/articleAudioPlayer').then(
    (mod) => mod.ArticleAudioPlayer,
  ),
)

type MobileBottomArticleNavigationProps = {
  className?: string
  isBookmarked?: boolean
  onBookmarkClick?: () => void
  progress?: number
  turnOffComment?: boolean
  turnOffArticleVoice?: boolean
  enableAudio?: boolean
  articleId?: string | number
  articleContent?: string
  isPremium?: boolean
  articleUrl?: string
}

type NavType = {
  type: string
  status: boolean
}

const MobileBottomArticleNavigation = ({
  isBookmarked,
  onBookmarkClick,
  turnOffComment,
  turnOffArticleVoice,
  enableAudio,
  articleId,
  articleContent,
  isPremium,
  articleUrl,
}: MobileBottomArticleNavigationProps) => {
  const {
    locale,
    router: { pathname },
  } = useRouterExtend()
  const {
    state: { zaloConfig, chatwootWebsiteToken },
  } = useContext(AppContext)
  useLatestTotalComment()
  const ref = useClickOutside(() => setShowChatBox(false))
  const currentUrl = useCurrentUrl()
  const { totalComment, setIsCommentPopupOpen } = useArticleStore(
    (state) => ({
      totalComment: state.totalComment,
      setIsCommentPopupOpen: state.setIsCommentPopupOpen,
    }),
    shallow,
  )
  const [showChatBox, setShowChatBox] = useState(false)
  useSpacingSubotStickyBar()
  const {
    state: { currentDomain },
  } = useContext(AppContext)
  const isCommentDisabled = useMemo(() => {
    return !LOCALE_HAS_COMMUNITY_SITE.includes(locale as LOCALE)
  }, [locale])

  const whatShouldRender = useMemo(
    () => ({
      voice: true,
      aiDoctor: HAS_CHAT_AI_SITE.includes(locale),
      healthTool: true,
      togetherComment: !isCommentDisabled,
      thirdPartyChat: zaloConfig?.oaid || chatwootWebsiteToken,
      bookmark: false,
      categories: true,
    }),
    [isCommentDisabled, zaloConfig?.oaid, chatwootWebsiteToken],
  )

  const prioritySort = useMemo(
    () => [
      'voice',
      'askDoctor',
      'aiDoctor',
      'categories',
      'healthtool',
      'jumpingLink',
      'bookmark',
    ],
    [],
  )

  const priorityByKey = useMemo(() => {
    const priority = [
      { type: 'bookmark', status: whatShouldRender.bookmark },
      {
        type: 'askDoctor',
        status: whatShouldRender?.togetherComment,
      },
      { type: 'voice', status: whatShouldRender.voice },
      { type: 'aiDoctor', status: whatShouldRender.aiDoctor },
      { type: 'healthtool', status: whatShouldRender.healthTool },
      { type: 'categories', status: whatShouldRender.categories },
    ]
      .filter((i) => i.status)
      .slice(0, 3) as NavType[]
    const tmp = {} as {
      [key: string]: NavType
    }
    priority.map((i) => {
      tmp[i.type] = i
    })
    return tmp
  }, [whatShouldRender])

  const handleScroll = useCallback(
    throttle(() => {
      showChatBox && setShowChatBox(false)
    }, 200),
    [showChatBox],
  )

  const renderAskDoctor = () => {
    const onClick = () => {
      if (!turnOffComment) setIsCommentPopupOpen(true)
    }

    return (
      <Box
        ref={ref}
        className="w-10 h-10 p-2 relative cursor-pointer"
        onClick={onClick}
      >
        <ChatBubble />
        {Number(totalComment) > 0 && (
          <Text
            size="p4"
            className="absolute top-2 left-8"
            color={theme.colors.gray600}
          >
            {totalComment}
          </Text>
        )}
      </Box>
    )
  }

  return (
    <>
      <Wrapper height={44} onScroll={handleScroll}>
        {ALLOW_AIR_POPUPLATION.includes(locale) && (
          <Box>
            <Box
              sx={{
                width: 40,
                '.chart-label': {
                  fontSize: rem(6),
                  lineHeight: rem(12),
                  marginBottom: 0,
                },
              }}
            >
              <AirPopulation size="sm">
                <AirPopulation.IconChart
                  dataTracking={{
                    dataEventCategory: 'Sticky bottom',
                    dataEventAction: 'AQI icon click',
                    dataEventLabel: 'AQI open pop-up',
                  }}
                />
              </AirPopulation>
            </Box>
          </Box>
        )}
        {prioritySort.map((i: string) =>
          i === 'bookmark' && priorityByKey?.[i] ? (
            <MobileBottomNavigation.Icon
              onClick={onBookmarkClick}
              icon={
                isBookmarked ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M6.775 19.7C6.475 19.8333 6.18733 19.8123 5.912 19.637C5.63733 19.4623 5.5 19.2083 5.5 18.875V5.3C5.5 4.8 5.675 4.375 6.025 4.025C6.375 3.675 6.8 3.5 7.3 3.5H16.7C17.2 3.5 17.625 3.675 17.975 4.025C18.325 4.375 18.5 4.8 18.5 5.3V18.875C18.5 19.2083 18.3623 19.4623 18.087 19.637C17.8123 19.8123 17.525 19.8333 17.225 19.7L12 17.45L6.775 19.7Z"
                      fill="#2D87F3"
                    />
                  </svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M7 17.95L12 15.8L17 17.95V5.3C17 5.23333 16.9667 5.16667 16.9 5.1C16.8333 5.03333 16.7667 5 16.7 5H7.3C7.23333 5 7.16667 5.03333 7.1 5.1C7.03333 5.16667 7 5.23333 7 5.3V17.95ZM6.775 19.7C6.475 19.8333 6.18733 19.8123 5.912 19.637C5.63733 19.4623 5.5 19.2083 5.5 18.875V5.3C5.5 4.8 5.675 4.375 6.025 4.025C6.375 3.675 6.8 3.5 7.3 3.5H16.7C17.2 3.5 17.625 3.675 17.975 4.025C18.325 4.375 18.5 4.8 18.5 5.3V18.875C18.5 19.2083 18.3623 19.4623 18.087 19.637C17.8123 19.8123 17.525 19.8333 17.225 19.7L12 17.45L6.775 19.7ZM7 5H17H12H7Z"
                      fill="#595959"
                    />
                  </svg>
                )
              }
              isActive={isBookmarked}
              data-event-category="Sticky Bottom"
              data-event-action="Bookmark Click"
              data-event-label={currentUrl}
              className="nav-item pointer-event-child-none"
              // label={f({
              //   id: 'mobileBar.save',
              //   defaultMessage: 'Save',
              // })}
            />
          ) : i === 'askDoctor' && priorityByKey?.[i] ? (
            renderAskDoctor()
          ) : i === 'aiDoctor' && priorityByKey?.[i] ? (
            <Box
              component="a"
              href={currentDomain + CHAT_AI_PATH}
              data-event-category="Chatbot AI"
              data-event-action="Open"
              data-event-label="Article action"
              className="w-10 h-10 py-2 relative cursor-pointer flex justify-center items-end pointer-event-child-none"
            >
              <ChatAi size={24} color="#2F8AF2" />
              <Text
                size="p5"
                className="!text-[10px]"
                weight="halfBold"
                as="span"
                color="#49A8EA"
                translate="no"
              >
                AI
              </Text>
            </Box>
          ) : i === 'voice' && priorityByKey?.[i] ? (
            <ArticleAudioPlayer
              articleId={articleId}
              articleContent={articleContent}
              articleEnableAudio={enableAudio}
              articleTurnOffArticleVoice={turnOffArticleVoice}
              isPremium={isPremium}
              isSidebar
              onError={(ref) => {
                if (ref) {
                  ref.style.display = 'none'
                }
              }}
              onLoading={(loaded, ref) => {
                if (ref) {
                  ref.style.display = loaded ? 'inherit' : 'none'
                }
              }}
            />
          ) : i === 'healthtool' && priorityByKey?.[i] ? (
            <a
              href={currentDomain + '/health-tools'}
              data-event-category="Sticky Bottom"
              data-event-action="Engage Click"
              data-event-label={currentUrl}
              className="nav-item pointer-event-child-none"
            >
              <MobileBottomNavigation.Icon
                icon={
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M10.6 15.35C10.95 15.7 11.4083 15.8667 11.975 15.85C12.5417 15.8333 12.95 15.6333 13.2 15.25L17.2 9.45C17.35 9.23333 17.3333 9.03767 17.15 8.863C16.9667 8.68767 16.7667 8.66667 16.55 8.8L10.75 12.8C10.3667 13.05 10.1583 13.45 10.125 14C10.0917 14.55 10.25 15 10.6 15.35ZM5.1 19.5C4.83333 19.5 4.58333 19.4373 4.35 19.312C4.11667 19.1873 3.925 19 3.775 18.75C3.39167 18.0833 3.08333 17.3583 2.85 16.575C2.61667 15.7917 2.5 14.9333 2.5 14C2.5 12.7 2.75 11.475 3.25 10.325C3.75 9.175 4.425 8.16667 5.275 7.3C6.125 6.43333 7.12933 5.75 8.288 5.25C9.446 4.75 10.6833 4.5 12 4.5C12.7667 4.5 13.4877 4.579 14.163 4.737C14.8377 4.89567 15.4917 5.13333 16.125 5.45C16.4083 5.6 16.571 5.79567 16.613 6.037C16.6543 6.279 16.575 6.50833 16.375 6.725C16.275 6.825 16.1377 6.88333 15.963 6.9C15.7877 6.91667 15.6333 6.9 15.5 6.85C14.95 6.56667 14.3873 6.35433 13.812 6.213C13.2373 6.071 12.6333 6 12 6C9.78333 6 7.896 6.779 6.338 8.337C4.77933 9.89567 4 11.7833 4 14C4 14.7 4.09567 15.3917 4.287 16.075C4.479 16.7583 4.75 17.4 5.1 18H18.9C19.2833 17.3667 19.5627 16.7083 19.738 16.025C19.9127 15.3417 20 14.6333 20 13.9C20 13.3167 19.9293 12.7333 19.788 12.15C19.646 11.5667 19.4333 11 19.15 10.45C19.0667 10.2833 19.0373 10.1207 19.062 9.962C19.0873 9.804 19.175 9.65833 19.325 9.525C19.525 9.35833 19.746 9.30833 19.988 9.375C20.2293 9.44167 20.4083 9.6 20.525 9.85C20.8417 10.5167 21.0793 11.175 21.238 11.825C21.396 12.475 21.4833 13.1583 21.5 13.875C21.5 14.775 21.396 15.6293 21.188 16.438C20.9793 17.246 20.6583 18.0333 20.225 18.8C20.0917 19.0167 19.9043 19.1873 19.663 19.312C19.421 19.4373 19.1667 19.5 18.9 19.5H5.1Z"
                      fill="#595959"
                    />
                  </svg>
                }
                // label={f({
                //   id: 'mobileBar.healthTools',
                //   defaultMessage: 'Health Screening',
                // })}
              />
            </a>
          ) : i === 'categories' && priorityByKey?.[i] ? (
            <Box
              component="a"
              href="/categories"
              data-event-category="Sticky Bottom"
              data-event-action="Home Click"
              data-event-label={currentUrl}
              className="nav-item pointer-event-child-none"
            >
              <MobileBottomNavigation.Icon
                icon={<Home isActive={pathname === '/categories'} />}
                isActive={pathname === '/categories'}
                // label={f({
                //   id: 'mobileBar.home',
                //   defaultMessage: 'Discover',
                // })}
              />
            </Box>
          ) : null,
        )}
        <SharePopupNew.BottomNavHorizonal
          shareUrl={articleUrl || ''}
          hasOverlay
          isRevamp
          copyInputProps={{
            popoverProps: {
              withinPortal: true,
              position: 'top',
            },
          }}
        />
      </Wrapper>
      <style>{CommonStyle}</style>
    </>
  )
}

export default MobileBottomArticleNavigation
